import { useEffect, useState, useContext, useCallback, useMemo, useRef } from "react";
import {
  getIssues, getIssueById, getActivities,
  updateIssueStatus, reassignIssue, addComment, deleteIssue, getTimeBasedStats, updateIssue, moveToSprint,
} from "../api/issueApi";
import { getProjects, getMyProjects } from "../api/projectApi";
import { getVisibleUsers } from "../api/userApi";
import { getSprints } from "../api/sprintApi";
import { useSearchParams } from "react-router-dom";
import { AuthContext } from "../auth/AuthContext";
import IssueForm from "../components/IssueForm";
import { issueFormModalPanelStyle } from "../constants/issueFormModal";
import toast from "react-hot-toast";
import { getPriorityColor, getStatusColor } from '../theme/colors';
import {
  issueStatusLabel,
  normalizeBugResolutionStatus,
  LEGACY_CANNOT_REPRODUCE_STATUS,
} from '../constants/issueStatuses';
import {
  getBugStatusOptionGroups,
  activeSprintBugCounts,
  allProjectsActiveSprintBugCounts,
  canUseFullBugStatusMenu,
  isDeveloperBugRole,
  isBugQaVerifiedClosed,
} from '../constants/bugWorkflow';
import { isQaTesterRole, normalizeAppRole } from '../utils/roleUtils';
import { COLORS as C, RADIUS, MONO } from "../constants/ui";
import Skeleton from "../components/ui/Skeleton";
import {
  canonicalTeamKey,
  resolveTeamDisplayName,
  teamsMatch,
} from '../utils/assigneeTeamFilter';
import { SPRINT_WORK_STATUSES } from '../constants/sprintAccess';

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIORITY_META = {
  Blocker:  { bg: C.dangerSoft, color: C.danger, dot: C.danger },
  Critical: { bg: C.dangerSoft, color: C.danger, dot: C.danger },
  High:     { bg: C.warnSoft, color: C.warn, dot: C.warn },
  Medium:   { bg: C.sunken, color: C.inkSoft, dot: C.line },
  Low:      { bg: C.sunken, color: C.inkFaint, dot: C.line },
  _default: { bg: C.sunken, color: C.inkSoft, dot: C.inkFaint },
};

const STATUS_META = {
  "Open":             { bg: C.sunken, color: C.inkSoft },
  "In Progress":      { bg: C.accentSoft, color: C.accentInk },
  "Testing":          { bg: C.accentSoft, color: C.accentInk },
  "Blocked":          { bg: C.warnSoft, color: C.warn },
  "Fixed":            { bg: C.doneSoft, color: C.done },
  "Not Reproducible": { bg: C.sunken, color: C.inkSoft },
  "Invalid":          { bg: C.warnSoft, color: C.warn },
  "Won't Fix":        { bg: C.sunken, color: C.inkSoft },
  "Duplicate":        { bg: C.sunken, color: C.inkSoft },
  "Closed":           { bg: C.sunken, color: C.inkFaint },
  "Reopened":         { bg: C.warnSoft, color: C.warn },
};

const TYPE_META = {
  Bug:   { bg: C.dangerSoft, color: C.danger },
  Task:  { bg: C.accentSoft, color: C.accent },
  Story: { bg: C.accentSoft, color: C.accent },
  Epic:  { bg: C.warnSoft, color: C.warn },
};

const RESOLUTION_META = {
  "Fixed":            { icon: "Fixed", color: C.done },
  "Not Reproducible": { icon: "Not Reproducible", color: C.inkSoft },
  "Invalid":          { icon: "Invalid", color: C.warn },
  "Won't Fix":        { icon: "Won't Fix", color: C.inkSoft },
  "Duplicate":        { icon: "Duplicate", color: C.inkSoft },
  "By Design":        { icon: "By Design", color: C.inkSoft },
  [LEGACY_CANNOT_REPRODUCE_STATUS]: { icon: "Not Reproducible", color: C.inkSoft },
};

const ACTIVITY_ICONS = {
  created:         { icon: "●", color: C.accent },
  status_changed:  { icon: "↺", color: C.accent },
  assigned:        { icon: "👤", color: C.accent },
  reassigned:      { icon: "⇄", color: C.accent },
  closed:          { icon: "✓", color: C.inkSoft },
  reopened:        { icon: "↩", color: C.warn },
  commented:       { icon: "💬", color: C.accent },
  updated:         { icon: "✎", color: C.inkSoft },
  moved_to_sprint: { icon: "⇢", color: C.accent },
};


const RESOLUTIONS = ["Fixed", "Not Reproducible", "Invalid", "Won't Fix", "Duplicate", "By Design"];
const CLOSED_STATUSES = ["Fixed", "Not Reproducible", "Invalid", "Won't Fix", "Duplicate", "Closed", LEGACY_CANNOT_REPRODUCE_STATUS];

/** Resolved-state filter values (date + QA verify) — single dropdown on Issues page. */
const RESOLVED_STATE_ALL = "all";
const RESOLVED_STATE_VERIFY_CLOSED = "verify_closed";
const RESOLVED_STATE_VERIFY_CLOSED_TODAY = "verify_closed_today";
const RESOLVED_STATE_VERIFY_CLOSED_YESTERDAY = "verify_closed_yesterday";
const RESOLVED_STATE_VERIFY_CLOSED_WEEK = "verify_closed_week";
const RESOLVED_STATE_VERIFY_REOPENED = "verify_reopened";

const VERIFY_CLOSED_DATE_FILTERS = {
  [RESOLVED_STATE_VERIFY_CLOSED_TODAY]: "today",
  [RESOLVED_STATE_VERIFY_CLOSED_YESTERDAY]: "yesterday",
  [RESOLVED_STATE_VERIFY_CLOSED_WEEK]: "week",
};

const ALL_STATUSES = ['Open', 'In Progress', 'Testing', 'Blocked', 'Reopened', 'Fixed', 'Not Reproducible', 'Invalid', "Won't Fix", 'Duplicate', 'By Design', 'Closed'];

// ─── Badge helpers ────────────────────────────────────────────────────────────

const PriorityBadge = ({ priority }) => {
  const m = PRIORITY_META[priority] || PRIORITY_META._default;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5,
      background: m.bg, color: m.color, padding: "2px 10px", borderRadius: 20, fontSize: "0.72rem", fontWeight: 700 }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: m.dot }} />
      {priority}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const { user } = useContext(AuthContext);
  const m = STATUS_META[status] || { bg: C.sunken, color: C.ink };
  return (
    <span style={{ background: m.bg, color: m.color, padding: "3px 10px",
      borderRadius: 20, fontSize: "0.72rem", fontWeight: 700, whiteSpace: "nowrap" }}>
      {issueStatusLabel(status, user?.department)}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const m = TYPE_META[type] || { bg: C.sunken, color: C.ink };
  return (
    <span style={{ background: m.bg, color: m.color, padding: "2px 8px",
      borderRadius: 6, fontSize: "0.72rem", fontWeight: 700 }}>
      {type}
    </span>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

/** `yyyy-MM-dd` for `<input type="date" />` */
const toDateInputValue = (d) => {
  if (!d) return "";
  const t = new Date(d);
  if (Number.isNaN(t.getTime())) return "";
  return t.toISOString().slice(0, 10);
};

const APP_TIMEZONE = "Asia/Kolkata";

const calendarDayInTz = (value, tz = APP_TIMEZONE) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(d);
};

const todayCalendarDay = (tz = APP_TIMEZONE) => calendarDayInTz(new Date(), tz);

const addCalendarDays = (ymd, delta) => {
  if (!ymd) return null;
  const [y, m, day] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, day));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
};

const matchesCalendarDateFilter = (value, mode, customFrom, customTo) => {
  const day = calendarDayInTz(value);
  if (!day) return false;
  const today = todayCalendarDay();
  switch (mode) {
    case "today":
      return day === today;
    case "yesterday":
      return day === addCalendarDays(today, -1);
    case "week": {
      const weekStart = addCalendarDays(today, -6);
      return day >= weekStart && day <= today;
    }
    case "custom": {
      if (customFrom && day < customFrom) return false;
      if (customTo && day > customTo) return false;
      return Boolean(customFrom || customTo);
    }
    default:
      return true;
  }
};

const fmtDateTime = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
  });
};

const daysAgo = (d) => {
  const day = calendarDayInTz(d);
  if (!day) return null;
  const today = todayCalendarDay();
  if (day === today) return "Today";
  if (day === addCalendarDays(today, -1)) return "Yesterday";
  const diff = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
  return diff >= 0 ? `${diff}d ago` : null;
};

const isClosed = (status) => CLOSED_STATUSES.includes(status);

/** QA-verified Closed — use updated_at when dev resolution already set closeddate. */
const qaVerifiedClosedAt = (issue) => issue.updated_at ?? issue.updatedAt ?? issue.closeddate;

const extractUrls = (text) => {
  if (!text) return [];
  const matches = text.match(/https?:\/\/[^\s,]+/gi) || [];
  return Array.from(new Set(matches));
};

const parseIssueDescription = (description) => {
  const text = (description || '').trim();
  if (!text) return { title: '', details: '', links: [] };

  const marker = 'image links:';
  const markerIndex = text.toLowerCase().indexOf(marker);

  const baseText = markerIndex >= 0 ? text.slice(0, markerIndex).trim() : text;
  const linksBlock = markerIndex >= 0 ? text.slice(markerIndex) : '';
  const links = extractUrls(linksBlock);

  const lines = baseText
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  return {
    title: lines[0] || '',
    details: lines.slice(1).join('\n').trim(),
    links,
  };
};

const LinkifiedText = ({ text }) => {
  if (!text) return null;
  const lines = text.split('\n');

  return (
    <>
      {lines.map((line, lineIndex) => {
        const segments = line.split(/(https?:\/\/[^\s]+)/gi);
        return (
          <span key={`line-${lineIndex}`}>
            {segments.map((part, idx) => (
              idx % 2 === 1 ? (
                <a key={`seg-${lineIndex}-${idx}`} href={part} target="_blank" rel="noreferrer" style={{ color: C.accentInk, textDecoration: 'underline', wordBreak: 'break-all' }}>
                  {part}
                </a>
              ) : (
                <span key={`seg-${lineIndex}-${idx}`}>{part}</span>
              )
            ))}
            {lineIndex < lines.length - 1 && <br />}
          </span>
        );
      })}
    </>
  );
};

// ─── Overlay / Modal shell ────────────────────────────────────────────────────
// Backdrops must not use onClick-to-close (accidental dismiss). Use ✕ / Cancel in the panel.

const modalBackdropStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 9999,
  background: "rgba(0,0,0,0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2rem 1rem",
  overflow: "hidden",
  overscrollBehavior: "none",
};

const issueModalPanelStyle = {
  background: "white",
  borderRadius: 14,
  width: "100%",
  maxWidth: 1200,
  maxHeight: "min(90vh, calc(100vh - 2rem))",
  overflowY: "auto",
  overflowX: "hidden",
  boxShadow: "0 25px 60px rgba(0,0,0,0.3)",
  overscrollBehavior: "contain",
};

// ─── Issue Detail Modal ───────────────────────────────────────────────────────

export const IssueDetailModal = ({
  issueId,
  onClose,
  onStatusChanged,
  users = [],
  onIssueUpdated,
  navigationIds = null,
  onNavigate = null,
}) => {
  const { user, role } = useContext(AuthContext);
  const currentRole = normalizeAppRole(role);
  const isAdmin = ["admin", "superadmin"].includes(currentRole);

  const [issue, setIssue]       = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);

  // Status transition state
  const [showResolveForm, setShowResolveForm] = useState(false);
  const [resolution, setResolution]           = useState("");
  const [statusComment, setStatusComment]     = useState("");

  // Comment state
  const [commentText, setCommentText]   = useState("");
  const [postingComment, setPostingComment] = useState(false);

  // Reassign state
  const [showReassign, setShowReassign] = useState(false);
  const [newAssignee, setNewAssignee]   = useState("");
  const [reassignComment, setReassignComment] = useState("");
  const [reassignError, setReassignError] = useState("");
  const [showPriorityEdit, setShowPriorityEdit] = useState(false);
  const [newPriority, setNewPriority] = useState("High");
  const [showDueDateEdit, setShowDueDateEdit] = useState(false);
  const [newDueDate, setNewDueDate] = useState("");
  const [sprintsForProject, setSprintsForProject] = useState([]);
  const [movingSprint, setMovingSprint] = useState(false);

  const navIndex = useMemo(() => {
    if (!Array.isArray(navigationIds) || navigationIds.length === 0) return -1;
    return navigationIds.findIndex((id) => String(id) === String(issueId));
  }, [navigationIds, issueId]);

  const prevIssueId = navIndex > 0 ? navigationIds[navIndex - 1] : null;
  const nextIssueId =
    navIndex >= 0 && navIndex < navigationIds.length - 1 ? navigationIds[navIndex + 1] : null;
  const showNav = typeof onNavigate === "function" && navIndex >= 0 && navigationIds.length > 1;

  /**
   * Held in a ref so the loader does not depend on it.
   *
   * onClose is passed as an inline arrow, so it is a new function every render.
   * With it in the dependency list, a failed load called onClose(), the parent
   * re-rendered, onClose got a fresh identity, useCallback rebuilt loadIssue,
   * and the effect fired again — a request storm and one toast per turn, which
   * is what filled the screen on a timeout.
   */
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  /**
   * Reload after an edit. Still a callback because six handlers below call it,
   * but it now depends only on issueId — closing on failure goes through the
   * ref above, so this identity is stable across the parent's re-renders.
   */
  const loadIssue = useCallback(
    ({ closeOnError = true } = {}) => {
      const requestId = issueId;
      return Promise.all([getIssueById(requestId), getActivities(requestId)])
        .then(([ir, ar]) => {
          if (String(ir.data?.issueid) !== String(requestId)) return;
          setIssue(ir.data);
          setNewPriority(ir.data?.priority || "High");
          setNewDueDate(toDateInputValue(ir.data?.due_date));
          setActivities(ar.data || []);
          setLoading(false);
        })
        .catch(() => {
          if (String(requestId) !== String(issueId)) return;
          toast.error("Could not load this issue — check your connection and try again");
          if (closeOnError) onCloseRef.current?.();
        });
    },
    [issueId]
  );

  // One opening attempt per issue id. Even if something destabilises this
  // effect later, a failure cannot turn into a request storm.
  const attemptedRef = useRef(null);
  useEffect(() => {
    if (!issueId) return;
    if (attemptedRef.current === String(issueId)) return;
    attemptedRef.current = String(issueId);
    loadIssue();
  }, [issueId, loadIssue]);

  useEffect(() => {
    setLoading(true);
    setIssue(null);
    setShowResolveForm(false);
    setResolution("");
    setStatusComment("");
  }, [issueId]);

  useEffect(() => {
    if (!issue?.projectid) {
      setSprintsForProject([]);
      return;
    }
    getSprints(issue.projectid)
      .then((r) => setSprintsForProject(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSprintsForProject([]));
  }, [issue?.projectid]);

  const handleSprintSelectChange = async (nextVal) => {
    if (!issue || issue.issuetype !== "Bug") return;
    const cur =
      issue.sprint_id != null && issue.sprint_id !== "" ? String(issue.sprint_id) : "";
    if (nextVal === cur) return;
    const payload = nextVal === "" ? null : Number.parseInt(String(nextVal), 10);
    if (payload !== null && Number.isNaN(payload)) {
      toast.error("Invalid sprint");
      return;
    }
    setMovingSprint(true);
    try {
      await moveToSprint(issueId, payload);
      await loadIssue();
      onIssueUpdated?.();
      toast.success(payload == null ? "Sprint cleared (backlog)" : "Sprint updated");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update sprint");
    }
    setMovingSprint(false);
  };

  // Simple one-click transition (no confirm needed)
  const quickTransition = async (status, resolution, comment) => {
    setSaving(true);
    try {
      const res = await updateIssueStatus(issueId, status, resolution, comment);
      setIssue(res.data.issue);
      onStatusChanged?.(issueId, status);
      loadIssue();
      toast.success(resolution ? `Resolved as ${resolution}` : `→ ${status}`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update");
    }
    setSaving(false);
  };


  const handleComment = async () => {
    if (!commentText.trim()) return;
    setPostingComment(true);
    try {
      await addComment(issueId, commentText);
      setCommentText("");
      loadIssue();
    } catch { toast.error("Failed to add comment"); }
    setPostingComment(false);
  };

  const handleReassign = async () => {
    setReassignError("");
    if (!newAssignee) {
      setReassignError("Please select a team member to assign.");
      return;
    }
    if (issue && String(newAssignee) === String(issue.assignee_id)) {
      setReassignError("This issue is already assigned to that person.");
      return;
    }
    setSaving(true);
    try {
      const res = await reassignIssue(issueId, newAssignee, reassignComment || undefined);
      setIssue(res.data.issue);
      setShowReassign(false);
      setNewAssignee("");
      setReassignComment("");
      setReassignError("");
      loadIssue();
      toast.success("Reassigned successfully");
    } catch (err) {
      setReassignError(err.response?.data?.message || "Failed to reassign");
    }
    setSaving(false);
  };

  const handlePrioritySave = async () => {
    if (!issue || !newPriority || newPriority === issue.priority) {
      setShowPriorityEdit(false);
      return;
    }
    setSaving(true);
    try {
      const res = await updateIssue(issueId, { priority: newPriority });
      setIssue(res.data.issue);
      loadIssue();
      toast.success("Priority updated");
      setShowPriorityEdit(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update priority");
    }
    setSaving(false);
  };

  const handleDueDateSave = async () => {
    if (!issue) return;
    const nextIso = newDueDate.trim();
    const prevIso = issue.due_date ? toDateInputValue(issue.due_date) : "";
    if (nextIso === prevIso) {
      setShowDueDateEdit(false);
      return;
    }
    setSaving(true);
    try {
      const res = await updateIssue(issueId, { due_date: nextIso || null });
      setIssue(res.data.issue);
      loadIssue();
      toast.success(nextIso ? "Due date updated" : "Due date cleared");
      setShowDueDateEdit(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update due date");
    }
    setSaving(false);
  };

  if (loading) return (
    <div style={modalBackdropStyle}>
      <div style={{ ...issueModalPanelStyle, maxWidth: 1200, padding: "2rem", textAlign: "center", color: C.inkSoft }}>
        Loading issue…
      </div>
    </div>
  );

  if (!issue) return null;
  const issueContent = parseIssueDescription(issue.description);
  const issueTitle = issueContent.title || '(No description)';
  const assignableUsers = users.filter(u => !['admin', 'superadmin'].includes(u.role?.toLowerCase()));
  const pm = PRIORITY_META[issue.priority] || PRIORITY_META.Medium;
  const closed = isClosed(issue.status);
  const isBug = issue.issuetype === "Bug";
  const isStoryTask = issue.issuetype === "Story" || issue.issuetype === "Task";
  const canChangeStatusHere = isBug || isStoryTask;
  const bugStatusGroups = isBug ? getBugStatusOptionGroups(currentRole) : null;
  const bugVerifyStatuses = (() => {
    if (!bugStatusGroups?.verify?.length) return [];
    if (isDeveloperBugRole(currentRole)) return [];
    return bugStatusGroups.verify;
  })();
  const curSprintIdStr =
    issue.sprint_id != null && issue.sprint_id !== "" ? String(issue.sprint_id) : "";
  const curSprintSelectable =
    !!curSprintIdStr
    && sprintsForProject.some(
      (s) => String(s.id) === curSprintIdStr && SPRINT_WORK_STATUSES.includes(s.status),
    );
  const showCurrentNonPlannableSprint =
    !!curSprintIdStr && issue.issuetype === "Bug" && !curSprintSelectable;
  const daysOpen = issue.closeddate
    ? Math.floor((new Date(issue.closeddate) - new Date(issue.createddate)) / 86400000)
    : Math.floor((Date.now() - new Date(issue.createddate).getTime()) / 86400000);

  return (
    <div style={modalBackdropStyle}>
      <div style={issueModalPanelStyle} onClick={e => e.stopPropagation()}>

        {/* ── Top color bar by priority ── */}
        <div style={{ height: 5, background: pm.dot }} />

        <div style={{ padding: "1.5rem 2rem" }}>
          {/* ── Header ── */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 7, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
                <TypeBadge type={issue.issuetype} />
                <PriorityBadge priority={issue.priority} />
                <StatusBadge status={issue.status} />
                {issue.resolution && (
                  <span style={{ fontSize: "0.75rem", fontWeight: 700, color: RESOLUTION_META[issue.resolution]?.color || C.inkSoft }}>
                    {RESOLUTION_META[issue.resolution]?.icon} {issue.resolution}
                  </span>
                )}
                <span style={{ color: C.inkFaint, fontSize: "0.78rem", fontWeight: 600 }}>#{issue.issueid}</span>
              </div>
              <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700, color: C.ink, lineHeight: 1.4 }}>
                {issueTitle}
              </h2>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 12, flexShrink: 0 }}>
              {showNav && (
                <>
                  <button
                    type="button"
                    disabled={!prevIssueId}
                    onClick={() => prevIssueId && onNavigate(prevIssueId)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 6,
                      border: `1px solid ${C.line}`,
                      background: prevIssueId ? "white" : C.sunken,
                      color: prevIssueId ? C.ink : C.upcoming,
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      cursor: prevIssueId ? "pointer" : "not-allowed",
                      whiteSpace: "nowrap",
                    }}
                  >
                    ← Prev
                  </button>
                  <button
                    type="button"
                    disabled={!nextIssueId}
                    onClick={() => nextIssueId && onNavigate(nextIssueId)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 6,
                      border: `1px solid ${C.line}`,
                      background: nextIssueId ? "white" : C.sunken,
                      color: nextIssueId ? C.ink : C.upcoming,
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      cursor: nextIssueId ? "pointer" : "not-allowed",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Next →
                  </button>
                  <span style={{ fontSize: "0.72rem", color: C.inkFaint, fontWeight: 600, whiteSpace: "nowrap" }}>
                    {navIndex + 1} / {navigationIds.length}
                  </span>
                </>
              )}
              <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: C.inkFaint }}>✕</button>
            </div>
          </div>

          {(issueContent.details || issueContent.links.length > 0) && (
            <div style={{ marginBottom: 16 }}>
              {issueContent.details && (
                <div style={{ fontSize: '0.84rem', color: C.ink, whiteSpace: 'pre-wrap', marginBottom: issueContent.links.length ? 8 : 0 }}>
                  <LinkifiedText text={issueContent.details} />
                </div>
              )}
              {issueContent.links.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.inkSoft, marginBottom: 4 }}>Image Links</div>
                  <div style={{ display: 'grid', gap: 4 }}>
                    {issueContent.links.map((url) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer" style={{ color: C.accentInk, textDecoration: 'underline', fontSize: '0.82rem', wordBreak: 'break-all' }}>
                        {url}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Meta grid ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.6rem 1.5rem",
            background: C.sunken, borderRadius: 10, padding: "1rem 1.25rem", marginBottom: 20 }}>
            <MR label="Project"     v={issue.projectname || issue.projectid} />
            <MR
              label="Sprint"
              v={
                issue.issuetype === "Bug" && issue.projectid ? (
                  <select
                    value={curSprintIdStr}
                    disabled={movingSprint || saving}
                    onChange={(e) => handleSprintSelectChange(e.target.value)}
                    style={{
                      ...selStyle,
                      width: "100%",
                      maxWidth: 280,
                      fontSize: "0.8rem",
                      opacity: movingSprint || saving ? 0.75 : 1,
                    }}
                  >
                    <option value="">
                      {curSprintIdStr ? "Backlog (no sprint)" : "Select a Sprint"}
                    </option>
                    {showCurrentNonPlannableSprint && (
                      <option value={curSprintIdStr} disabled>
                        {issue.sprint_name || `Sprint #${curSprintIdStr}`}
                        {issue.sprint_status ? ` (${issue.sprint_status})` : ""}
                      </option>
                    )}
                    {sprintsForProject
                      .filter((s) => SPRINT_WORK_STATUSES.includes(s.status))
                      .map((s) => (
                        <option key={s.id} value={String(s.id)}>
                          {s.sprint_name} ({s.status})
                        </option>
                      ))}
                  </select>
                ) : issue.sprint_name ? (
                  <span>
                    {issue.sprint_name}
                    {issue.sprint_status && String(issue.sprint_status).toLowerCase() !== "active" && (
                      <span style={{ marginLeft: 6, fontSize: "0.68rem", fontWeight: 700, color: C.inkSoft }}>
                        ({issue.sprint_status})
                      </span>
                    )}
                  </span>
                ) : (
                  "—"
                )
              }
            />
            <MR label="Team"        v={issue.assigneeteam ? resolveTeamDisplayName(issue.assigneeteam) : "—"} />
            <MR label="Reported by" v={issue.reporter_name || issue.reporter_email || "—"} />
            <MR label="Assigned to"
              v={
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  {issue.assignee_name || issue.assignee_email || "Unassigned"}
                  <button onClick={() => { setShowReassign(r => !r); setReassignError(''); setNewAssignee(issue?.assignee_id ? String(issue.assignee_id) : ''); }}
                    style={{ padding: "1px 7px", fontSize: "0.68rem", border: `1px solid ${C.line}`,
                      borderRadius: 4, cursor: "pointer", background: "white", color: C.inkSoft }}>
                    {showReassign ? "Cancel" : "Reassign"}
                  </button>
                </span>
              }
            />
            {issue.assigned_by_name && <MR label="Assigned by" v={issue.assigned_by_name} />}
            <MR label="Story Points" v={issue.story_points || "—"} />
            <MR label="Priority"
              v={
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <PriorityBadge priority={issue.priority} />
                  <button
                    onClick={() => setShowPriorityEdit(v => !v)}
                    style={{ padding: "1px 7px", fontSize: "0.68rem", border: `1px solid ${C.line}`, borderRadius: 4, cursor: "pointer", background: "white", color: C.inkSoft }}
                  >
                    {showPriorityEdit ? "Cancel" : "Change"}
                  </button>
                </span>
              }
            />
            <MR label="Due Date"
              v={
                <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span>{fmtDate(issue.due_date)}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowDueDateEdit((v) => !v);
                      setNewDueDate(toDateInputValue(issue.due_date));
                    }}
                    style={{ padding: "1px 7px", fontSize: "0.68rem", border: `1px solid ${C.line}`,
                      borderRadius: 4, cursor: "pointer", background: "white", color: C.inkSoft }}
                  >
                    {showDueDateEdit ? "Cancel" : "Change"}
                  </button>
                </span>
              }
            />
            <MR label="Days Open"
              v={
                <span style={{ fontWeight: 700, color: closed ? C.done : pm.color }}>
                  {daysOpen} day{daysOpen !== 1 ? "s" : ""} {closed ? "(closed)" : "(open)"}
                </span>
              }
            />
          </div>

          {/* ── Reassign panel ── */}
          {showReassign && (
            <div style={{ background: C.warnSoft, borderRadius: 8, padding: "0.9rem 1rem", marginBottom: 16,
              border: `1px solid ${C.warnSoft}` }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 700, color: C.warn, marginBottom: 8 }}>Reassign Issue</div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}>
                  <label style={lblStyle}>Assign to</label>
                  <select value={newAssignee} onChange={e => { setNewAssignee(e.target.value); setReassignError(''); }} style={selStyle}>
                    <option value="">-- Select person --</option>
                    {assignableUsers.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email} ({u.role})</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={lblStyle}>Reason (optional)</label>

                  <input value={reassignComment} onChange={e => setReassignComment(e.target.value)}
                    placeholder="Please check" style={selStyle} />
                </div>
                <button onClick={handleReassign} disabled={saving}
                  style={{ padding: "7px 16px", background: C.warn, color: "white", border: "none",
                    borderRadius: 7, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", height: 36 }}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
              {reassignError && (
                <div style={{ marginTop: 8, fontSize: '0.8rem', color: C.danger, fontWeight: 600 }}>
                  ⚠ {reassignError}
                </div>
              )}
            </div>
          )}

          {showPriorityEdit && (
            <div style={{ background: C.accentSoft, borderRadius: 8, padding: "0.9rem 1rem", marginBottom: 16,
              border: `1px solid ${C.accentSoft}` }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 700, color: C.accentInk, marginBottom: 8 }}>Update Priority</div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div style={{ minWidth: 180 }}>
                  <label style={lblStyle}>Priority</label>
                  <select value={newPriority} onChange={e => setNewPriority(e.target.value)} style={selStyle}>
                    {Object.keys(PRIORITY_META).filter(p => p !== '_default').map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <button onClick={handlePrioritySave} disabled={saving || newPriority === issue.priority}
                  style={{ padding: "7px 16px", background: C.accentInk, color: "white", border: "none",
                    borderRadius: 7, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", height: 36,
                    opacity: (saving || newPriority === issue.priority) ? 0.6 : 1 }}>
                  Save
                </button>
              </div>
            </div>
          )}

          {showDueDateEdit && (
            <div style={{ background: C.accentSoft, borderRadius: 8, padding: "0.9rem 1rem", marginBottom: 16,
              border: `1px solid ${C.accentSoft}` }}>
              <div style={{ fontSize: "0.82rem", fontWeight: 700, color: C.accentInk, marginBottom: 8 }}>Due date</div>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div>
                  <label style={lblStyle}>Date</label>
                  <input
                    type="date"
                    value={newDueDate}
                    onChange={(e) => setNewDueDate(e.target.value)}
                    style={{ ...selStyle, minWidth: 160 }}
                  />
                </div>
                <button type="button" onClick={() => setNewDueDate("")} disabled={saving || !newDueDate}
                  style={{ padding: "7px 12px", background: "white", color: C.inkSoft, border: `1px solid ${C.line}`,
                    borderRadius: 7, fontWeight: 600, fontSize: "0.82rem", cursor: "pointer", height: 36,
                    opacity: !newDueDate ? 0.5 : 1 }}>
                  Clear
                </button>
                <button type="button" onClick={handleDueDateSave} disabled={saving}
                  style={{ padding: "7px 16px", background: C.accentInk, color: "white", border: "none",
                    borderRadius: 7, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", height: 36,
                    opacity: saving ? 0.6 : 1 }}>
                  Save
                </button>
              </div>
            </div>
          )}

          {/* ── Timeline summary (bug lifecycle vs story/task “done”) ── */}
          <div style={{ display: "flex", gap: 0, alignItems: "center", marginBottom: 20 }}>
            <TS label={isBug ? "Raised" : "Created"} date={issue.createddate} color={C.accent} done />
            <TL />
            <TS label="Updated" date={issue.updated_at} color={C.warn} done={!!issue.updated_at} />
            <TL />
            <TS
              label={isBug ? "Closed" : "Done"}
              date={issue.closeddate || (closed ? issue.updated_at : null)}
              color={C.done}
              done={closed}
            />
          </div>

          {/* ── Resolution comment (if closed) ── */}
          {issue.resolution_comment && (
            <div style={{ background: C.doneSoft, border: `1px solid ${C.doneSoft}`, borderRadius: 8,
              padding: "0.7rem 1rem", marginBottom: 16, fontSize: "0.85rem", color: C.done }}>
              <strong>Resolution note:</strong> {issue.resolution_comment}
            </div>
          )}

          {/* ── Status Change Dropdown ── */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <StatusBadge status={issue.status} />
              {issue.resolution && (() => {
                const resolutionLabel = normalizeBugResolutionStatus(issue.resolution);
                const meta = RESOLUTION_META[issue.resolution] || RESOLUTION_META[resolutionLabel];
                return (
                  <span style={{ fontSize: "0.75rem", fontWeight: 700, color: meta?.color || C.inkSoft }}>
                    {meta?.icon || resolutionLabel} {resolutionLabel}
                  </span>
                );
              })()}

              {canChangeStatusHere ? (
              <select
                value=""
                onChange={(e) => {
                  const newStatus = e.target.value;
                  if (!newStatus) return;
                  if (isBug && bugStatusGroups && !canUseFullBugStatusMenu(currentRole)) {
                    if (isQaTesterRole(currentRole)) {
                      quickTransition(newStatus);
                      return;
                    }
                    if (bugStatusGroups.showClosedResolution && RESOLUTIONS.includes(newStatus)) {
                      setResolution(newStatus);
                      setShowResolveForm(true);
                      return;
                    }
                    quickTransition(newStatus);
                    return;
                  }
                  if (isBug && CLOSED_STATUSES.includes(newStatus)) {
                    setResolution(newStatus === "Closed" ? "" : newStatus);
                    setShowResolveForm(true);
                  } else if (isStoryTask && newStatus === "Closed") {
                    quickTransition("Closed");
                  } else {
                    quickTransition(newStatus);
                  }
                }}
                disabled={saving}
                style={{ padding: "5px 12px", borderRadius: 6, border: `1px solid ${C.upcoming}`,
                  background: "white", color: C.ink, fontSize: "0.82rem", cursor: "pointer",
                  minWidth: 140 }}
              >
                <option value="">Change Status…</option>
                {isBug && bugStatusGroups ? (
                  <>
                    {bugStatusGroups.active.length > 0 && (
                      <optgroup label="Active">
                        {bugStatusGroups.active.map(s => (
                          <option key={s} value={s}>{issueStatusLabel(s, user?.department)}</option>
                        ))}
                      </optgroup>
                    )}
                    {bugStatusGroups.closed.length > 0 && (
                      <optgroup label="Closed">
                        {bugStatusGroups.closed.map(r => <option key={r} value={r}>{r}</option>)}
                      </optgroup>
                    )}
                    {bugVerifyStatuses.length > 0 && (
                      <optgroup label="Verify">
                        {bugVerifyStatuses.map(s => (
                          <option key={s} value={s}>{s === "Reopened" ? "Reopen" : s}</option>
                        ))}
                      </optgroup>
                    )}
                  </>
                ) : (
                  <>
                    <optgroup label="Active">
                      {['Open', 'In Progress', 'Testing', 'Blocked'].map(s => (
                        <option key={s} value={s}>{issueStatusLabel(s, user?.department)}</option>
                      ))}
                    </optgroup>
                    {isBug && !isDeveloperBugRole(currentRole) && !isQaTesterRole(currentRole) ? (
                      <optgroup label="Closed">
                        {RESOLUTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </optgroup>
                    ) : !isBug ? (
                      <optgroup label="Done">
                        <option value="Closed">{issueStatusLabel('Done', user?.department)}</option>
                      </optgroup>
                    ) : null}
                    {isBug && isQaTesterRole(currentRole) && (
                      <optgroup label="Verify">
                        <option value="Closed">Closed</option>
                        <option value="Reopened">Reopen</option>
                      </optgroup>
                    )}
                  </>
                )}
              </select>
              ) : (
                <span style={{ fontSize: "0.8rem", color: C.inkSoft }}>
                  Status for {issue.issuetype || "this"} items is managed elsewhere (e.g. epic workflow or sprint tools).
                </span>
              )}
            </div>

            {/* Inline resolve form — bugs only, when a closed/resolution status is selected */}
            {isBug && showResolveForm && (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap",
                marginTop: 10, padding: "10px 12px", background: C.doneSoft,
                borderRadius: 8, border: `1px solid ${C.doneSoft}` }}>
                <div>
                  <label style={lblStyle}>Resolution *</label>
                  <select value={resolution} onChange={e => setResolution(e.target.value)}
                    style={{ ...selStyle, minWidth: 175 }}>
                    <option value="">Select…</option>
                    {RESOLUTIONS.map(r => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <label style={lblStyle}>Note (optional)</label>
                  <input value={statusComment} onChange={e => setStatusComment(e.target.value)}
                    placeholder="e.g. Fixed in commit abc123" style={selStyle} />
                </div>
                <button onClick={() => {
                  if (!resolution) { toast.error("Please select a resolution"); return; }
                  const status = CLOSED_STATUSES.includes(resolution) ? resolution : "Closed";
                  quickTransition(status, resolution, statusComment || undefined);
                  setShowResolveForm(false);
                  setResolution("");
                  setStatusComment("");
                }} disabled={saving || !resolution}
                  style={{ padding: "7px 16px", background: C.done, color: "white", border: "none",
                    borderRadius: 7, fontWeight: 700, fontSize: "0.82rem", cursor: "pointer",
                    whiteSpace: "nowrap", opacity: !resolution ? 0.5 : 1 }}>
                  {saving ? "Saving…" : "Mark Resolved"}
                </button>
                <button onClick={() => { setShowResolveForm(false); setResolution(""); setStatusComment(""); }}
                  disabled={saving}
                  style={{ padding: "7px 12px", background: C.sunken, color: C.inkSoft, border: `1px solid ${C.upcoming}`,
                    borderRadius: 7, fontWeight: 600, fontSize: "0.82rem", cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            )}
          </div>

          {/* ── Add Comment ── */}
          <div style={{ marginBottom: 24 }}>
            <div style={sectionTitle}>Add Comment</div>
            <div style={{ display: "flex", gap: 8 }}>
              <textarea value={commentText} onChange={e => setCommentText(e.target.value)}
                rows={2}
                placeholder={
                  isBug
                    ? "Add a note, steps to reproduce, fix details…"
                    : "Add a note or implementation detail…"
                }
                style={{ flex: 1, padding: "8px 12px", border: `1px solid ${C.line}`, borderRadius: 8,
                  fontSize: "0.85rem", resize: "vertical", fontFamily: "inherit" }} />
              <button onClick={handleComment} disabled={postingComment || !commentText.trim()}
                style={{ padding: "0 16px", background: C.done, color: "white", border: "none",
                  borderRadius: 8, fontWeight: 700, fontSize: "0.85rem", cursor: "pointer", alignSelf: "stretch" }}>
                {postingComment ? "…" : "Post"}
              </button>
            </div>
          </div>

          {/* ── Activity Feed ── */}
          <div>
            <div style={sectionTitle}>Activity ({activities.length})</div>
            {activities.length === 0 ? (
              <div style={{ color: C.inkFaint, fontSize: "0.85rem", padding: "0.5rem 0" }}>No activity yet.</div>
            ) : (
              <div style={{ maxHeight: 320, overflowY: "auto", paddingRight: 4 }}>
                {[...activities].reverse().map(a => {
                  const meta = ACTIVITY_ICONS[a.action_type] || ACTIVITY_ICONS.updated;
                  const actor = a.actor_name || a.actor_email || "System";
                  return (
                    <div key={a.id} style={{ display: "flex", gap: 12, paddingBottom: 14, position: "relative" }}>
                      {/* timeline line */}
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 32 }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: meta.color + "18",
                          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>
                          {meta.icon}
                        </div>
                        <div style={{ flex: 1, width: 2, background: C.sunken, marginTop: 4 }} />
                      </div>
                      <div style={{ flex: 1, paddingTop: 3 }}>
                        <div style={{ fontSize: "0.82rem", color: C.ink, marginBottom: 2 }}>
                          <strong style={{ color: C.ink }}>{actor}</strong>{" "}
                          <span style={{ color: meta.color, fontWeight: 600 }}>
                            {a.action_type.replace(/_/g, " ")}
                          </span>
                          {a.field_changed && a.field_changed !== "status" && (
                            <span style={{ color: C.inkSoft }}> {a.field_changed}</span>
                          )}
                          {a.old_value && a.new_value && (
                            <span style={{ color: C.inkSoft }}>
                              {" "}
                              <span style={{ textDecoration: "line-through", color: C.inkFaint }}>{a.old_value}</span>
                              {" → "}
                              <strong style={{ color: C.ink }}>{a.new_value}</strong>
                            </span>
                          )}
                          {!a.old_value && a.new_value && (
                            <span style={{ color: C.ink }}> → <strong>{a.new_value}</strong></span>
                          )}
                        </div>
                        {a.comment && a.action_type !== "created" && (
                          <div style={{ background: a.action_type === "commented" ? C.doneSoft : C.sunken,
                            borderLeft: `3px solid ${meta.color}`, padding: "6px 10px",
                            borderRadius: "0 6px 6px 0", fontSize: "0.82rem", color: C.ink,
                            marginTop: 4, whiteSpace: "pre-wrap" }}>
                            <LinkifiedText text={a.comment} />
                          </div>
                        )}
                        <div style={{ fontSize: "0.7rem", color: C.inkFaint, marginTop: 4 }}>
                          {fmtDateTime(a.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Sub-components
const MR = ({ label, v }) => (
  <div>
    <div style={{ fontSize: "0.7rem", color: C.inkFaint, fontWeight: 600, textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: "0.85rem", color: C.ink, fontWeight: 500 }}>{v}</div>
  </div>
);

const TS = ({ label, date, color, done }) => (
  <div style={{ textAlign: "center", minWidth: 80 }}>
    <div style={{ width: 26, height: 26, borderRadius: "50%", background: done ? color : C.line,
      display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 4px" }}>
      {done && <span style={{ color: "white", fontSize: 12, fontWeight: 700 }}>✓</span>}
    </div>
    <div style={{ fontSize: "0.7rem", fontWeight: 700, color: done ? color : C.inkFaint }}>{label}</div>
    {date && <div style={{ fontSize: "0.65rem", color: C.inkFaint, marginTop: 1 }}>{fmtDate(date)}</div>}
  </div>
);

const TL = () => <div style={{ flex: 1, height: 2, background: C.line, marginBottom: 28 }} />;

const sectionTitle = {
  fontSize: "0.75rem", fontWeight: 700, color: C.inkSoft,
  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8,
};

const lblStyle = {
  display: "block", fontSize: "0.75rem", fontWeight: 600, color: C.ink, marginBottom: 3,
};

const selStyle = {
  width: "100%", padding: "6px 10px", border: `1px solid ${C.line}`,
  borderRadius: 7, fontSize: "0.82rem", background: "white", boxSizing: "border-box",
};

// ─── Main Issues Page ─────────────────────────────────────────────────────────

/** The filter bar's selects — one shape, previously copy-pasted 12 times. */
const filterControlStyle = {
  padding: "6px 10px",
  border: `1px solid ${C.line}`,
  borderRadius: RADIUS.control,
  fontSize: "0.82rem",
  background: C.sunken,
};

const PRIORITY_FILTERS = ["All", "Blocker", "Critical", "High"];
const STATUS_FILTER_GROUPS = {
  "Active": ["Open", "In Progress", "Testing", "Blocked"],
  "Resolved": ["Fixed", "Not Reproducible", "Invalid", "Won't Fix", "Duplicate", "By Design"],
  "Verify": ["Closed", "Reopened"],
};

/** Status dropdown: "All" = every status; "All+Closed" = same for matching, but table does not hide resolved (see filtered list). */
const STATUS_ALL_INCLUDING_CLOSED = "All+Closed";

// ─── Project picker carousel (same interaction model as Sprint Board) ─────────

const ISSUES_CAROUSEL_EDGE_W = 48;
const ISSUES_CAROUSEL_ARROW_MIN = 3;

const issuesPickerCarouselOuter = {
  overflowX: "auto",
  overflowY: "hidden",
  WebkitOverflowScrolling: "touch",
  maxWidth: "100%",
  paddingBottom: 6,
  scrollbarWidth: "thin",
};
const issuesPickerCarouselInner = {
  display: "flex",
  gap: 10,
  flexWrap: "nowrap",
  width: "max-content",
  paddingTop: 2,
  paddingLeft: 2,
  paddingRight: 2,
};
const issuesPickerCarouselScrollTrack = {
  ...issuesPickerCarouselOuter,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
};
const issuesPickerCarouselShell = {
  position: "relative",
  width: "100%",
  maxWidth: "100%",
  overflow: "visible",
};
const issuesCarouselEdgeButtonBase = {
  position: "absolute",
  top: 0,
  bottom: 0,
  width: ISSUES_CAROUSEL_EDGE_W,
  zIndex: 40,
  border: "none",
  padding: 0,
  margin: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: C.accentInk,
  color: "#fff",
  fontSize: "1.4rem",
  fontWeight: 700,
  lineHeight: 1,
  cursor: "pointer",
  boxShadow: "0 2px 10px rgba(15, 23, 42, 0.25)",
  transition: "opacity 0.15s, background 0.15s",
};

function useIssuesCarouselScrollNav(contentStamp) {
  const ref = useRef(null);
  const [edge, setEdge] = useState({ canLeft: false, canRight: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) {
      setEdge({ canLeft: false, canRight: false });
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = Math.max(0, scrollWidth - clientWidth);
    setEdge({
      canLeft: scrollLeft > 4,
      canRight: scrollLeft < maxScroll - 4,
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const schedule = () => requestAnimationFrame(update);
    schedule();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [update, contentStamp]);

  const scrollStep = useCallback((direction) => {
    const el = ref.current;
    if (!el) return;
    const step = Math.max(220, Math.floor(el.clientWidth * 0.72));
    el.scrollBy({ left: direction * step, behavior: "smooth" });
  }, []);

  return { ref, edge, scrollStep };
}

/** Latest activity time for bugs on a project (for carousel sort). */
function maxBugActivityMs(projectId, issuesList) {
  const pid = String(projectId);
  let max = 0;
  for (const i of issuesList) {
    if (String(i.projectid) !== pid) continue;
    const raw = i.updated_at ?? i.updatedAt ?? i.closeddate ?? i.createddate;
    if (!raw) continue;
    const ms = new Date(raw).getTime();
    if (Number.isFinite(ms) && ms > max) max = ms;
  }
  return max;
}

const Issues = () => {
  const { user, role } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentRole = normalizeAppRole(role);
  const isAdmin = ["admin", "superadmin"].includes(currentRole);

  const [issues, setIssues]   = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers]     = useState([]);
  const [sprints, setSprints] = useState([]);
  const [timeStats, setTimeStats] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  /** Snapshot of filtered issue ids when modal opens — kept while navigating so resolved bugs stay in sequence. */
  const [modalNavIds, setModalNavIds] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Filters seed from the URL so a filtered view can be bookmarked, shared,
  // or survive a reload. `initialParams` is read once — after mount the
  // querystring is written from state, never read back into it, so typing in
  // a filter can't fight the effect below.
  const [initialParams] = useState(() => new URLSearchParams(window.location.search));
  const seed = (key, fallback) => initialParams.get(key) ?? fallback;

  const [search, setSearch]               = useState(() => seed("q", ""));
  const [filterPriority, setPriority]     = useState(() => seed("priority", "All"));
  const [filterStatus, setStatus]         = useState(() => seed("status", "All"));
  const [filterProject, setProject]       = useState(() => seed("project", "All"));
  const [filterAssignee, setFilterAssignee] = useState(() => seed("assignee", "All"));
  const [filterReporter, setFilterReporter] = useState(() => seed("reporter", "All"));
  const [filterTeam, setFilterTeam] = useState(() => seed("team", "All"));
  /** all | active | inactive | none — scope by sprint lifecycle (bugs on completed sprints stay visible when not filtering to active only). */
  const [filterSprintScope, setFilterSprintScope] = useState(() => seed("sprintScope", "all"));
  /** A specific sprint id, or "All" — narrows within whatever sprintScope selected. */
  const [filterSprintId, setFilterSprintId] = useState(() => seed("sprintId", "All"));
  const [showClosedOnly, setShowClosedOnly] = useState(() => initialParams.get("closedOnly") === "1");
  /** all | today | yesterday | week | custom — filter by bug resolution (closed) date */
  const [filterResolutionDate, setFilterResolutionDate] = useState(() => seed("resolvedOn", "all"));
  const [filterResolvedFrom, setFilterResolvedFrom] = useState(() => seed("resolvedFrom", ""));
  const [filterResolvedTo, setFilterResolvedTo] = useState(() => seed("resolvedTo", ""));
  /** all | today | yesterday | week | custom — filter by bug filed (created) date */
  const [filterReportedDate, setFilterReportedDate] = useState(() => seed("reportedOn", "all"));
  const [filterReportedFrom, setFilterReportedFrom] = useState(() => seed("reportedFrom", ""));
  const [filterReportedTo, setFilterReportedTo] = useState(() => seed("reportedTo", ""));

  const assigneeOptions = useMemo(() => {
    const scopedIssues =
      filterTeam === "All"
        ? issues
        : issues.filter((i) => {
            const team = (i.assigneeteam || "").trim();
            if (filterTeam === "_none") return !team;
            return teamsMatch(team, filterTeam);
          });
    const m = new Map();
    scopedIssues.forEach(i => {
      const id = i.assignee_id;
      const key = id == null ? "_none" : String(id);
      if (!m.has(key)) {
        m.set(key, {
          value: key,
          label: id == null ? "Unassigned" : (i.assignee_name || i.assignee_email || `User #${id}`),
        });
      }
    });
    return Array.from(m.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [issues, filterTeam]);

  const reporterOptions = useMemo(() => {
    const m = new Map();
    issues.forEach(i => {
      // Use reporter_id if available, otherwise fall back to reporter_name or reporter_email
      const id = i.reporter_id;
      const name = i.reporter_name || i.reporter_email;
      const key = id != null ? String(id) : (name ? `name_${name}` : "_none");
      if (!m.has(key)) {
        m.set(key, {
          value: key,
          label: name || (id != null ? `User #${id}` : "Unknown"),
        });
      }
    });
    return Array.from(m.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [issues]);

  const teamOptions = useMemo(() => {
    const seen = new Set();
    const rows = [];
    let hasEmpty = false;
    issues.forEach((i) => {
      const raw = (i.assigneeteam || "").trim();
      if (!raw) {
        hasEmpty = true;
        return;
      }
      const key = canonicalTeamKey(raw);
      if (seen.has(key)) return;
      seen.add(key);
      const label = resolveTeamDisplayName(raw);
      rows.push({ value: label, label });
    });
    rows.sort((a, b) => a.label.localeCompare(b.label));
    if (hasEmpty) rows.unshift({ value: "_none", label: "No team set" });
    return rows;
  }, [issues]);

  const load = useCallback(() => {
    setLoading(true);
    // Use getMyProjects for developers/testers, getProjects for admins/managers
    const projectsFn = ['developer', 'tester', 'designer', 'devops', 'analyst', 'member'].includes(currentRole)
      ? getMyProjects
      : getProjects;
    
    Promise.all([
      getIssues().catch(() => ({ data: [] })),
      projectsFn().catch(() => ({ data: [] })),
      getVisibleUsers().catch(() => ({ data: [] })),
      getSprints().catch(() => ({ data: [] })),
    ])
      .then(([ir, pr, ur, sr]) => {
        setIssues(ir.data || []);
        setProjects(pr.data || []);
        setUsers(ur.data || []);
        setSprints(sr.data || []);
      })
      .catch(err => {
        console.error("Failed to load Issues page data:", err);
        toast.error("Failed to load data");
      })
      .finally(() => setLoading(false));
  }, [currentRole]);

  const loadTimeStats = useCallback(() => {
    const params = filterProject !== "All" ? { projectId: filterProject } : {};
    return getTimeBasedStats(params)
      .then((r) => setTimeStats(r.data || null))
      .catch(() => setTimeStats(null));
  }, [filterProject]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { loadTimeStats(); }, [loadTimeStats]);

  const refreshAll = useCallback(() => {
    load();
    loadTimeStats();
  }, [load, loadTimeStats]);

  useEffect(() => {
    if (!selectedId && !showForm) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [selectedId, showForm]);

  useEffect(() => {
    const openIssueId = searchParams.get("openIssueId");
    if (!openIssueId) return;

    setSelectedId(openIssueId);
    const next = new URLSearchParams(searchParams);
    next.delete("openIssueId");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Mirror the active filters into the querystring so the view is shareable.
  // Only non-default values are written, keeping a clean URL when nothing is
  // filtered. `replace` so filtering doesn't fill up browser history.
  useEffect(() => {
    const next = new URLSearchParams();
    const put = (key, value, fallback) => {
      if (value !== fallback && value !== "" && value != null) next.set(key, value);
    };
    put("q", search, "");
    put("priority", filterPriority, "All");
    put("status", filterStatus, "All");
    put("project", filterProject, "All");
    put("assignee", filterAssignee, "All");
    put("reporter", filterReporter, "All");
    put("team", filterTeam, "All");
    put("sprintScope", filterSprintScope, "all");
    put("sprintId", filterSprintId, "All");
    if (showClosedOnly) next.set("closedOnly", "1");
    put("resolvedOn", filterResolutionDate, "all");
    put("resolvedFrom", filterResolvedFrom, "");
    put("resolvedTo", filterResolvedTo, "");
    put("reportedOn", filterReportedDate, "all");
    put("reportedFrom", filterReportedFrom, "");
    put("reportedTo", filterReportedTo, "");

    setSearchParams(next, { replace: true });
  }, [
    search, filterPriority, filterStatus, filterProject, filterAssignee,
    filterReporter, filterTeam, filterSprintScope, filterSprintId, showClosedOnly,
    filterResolutionDate, filterResolvedFrom, filterResolvedTo,
    filterReportedDate, filterReportedFrom, filterReportedTo,
    setSearchParams,
  ]);

  const handleStatusChanged = (issueId, status) => {
    setIssues(prev => prev.map(i =>
      String(i.issueid) === String(issueId)
        ? { ...i, status, closeddate: isClosed(status) ? new Date().toISOString() : (status === "Reopened" ? null : i.closeddate) }
        : i
    ));
  };

  const handleDelete = async (issueId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this issue permanently?")) return;
    try {
      await deleteIssue(issueId);
      setIssues(prev => prev.filter(i => i.issueid !== issueId));
      toast.success("Issue deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const filteredCore = issues.filter(i => {
    if (filterPriority !== "All" && i.priority !== filterPriority) return false;
    const statusOpenEnded = filterStatus === "All" || filterStatus === STATUS_ALL_INCLUDING_CLOSED;
    if (!statusOpenEnded && i.status !== filterStatus) return false;
    if (filterProject !== "All" && String(i.projectid) !== String(filterProject)) return false;
    if (filterAssignee !== "All") {
      if (filterAssignee === "_none") {
        if (i.assignee_id != null) return false;
      } else if (String(i.assignee_id) !== filterAssignee) return false;
    }
    if (filterReporter !== "All") {
      if (filterReporter === "_none") {
        if (i.reporter_id != null || i.reporter_name || i.reporter_email) return false;
      } else if (filterReporter.startsWith("name_")) {
        // Handle name-based keys
        const name = filterReporter.replace("name_", "");
        if (i.reporter_name !== name && i.reporter_email !== name) return false;
      } else if (String(i.reporter_id) !== filterReporter) return false;
    }
    if (filterTeam !== "All") {
      const team = (i.assigneeteam || "").trim();
      if (filterTeam === "_none") {
        if (team) return false;
      } else if (!teamsMatch(team, filterTeam)) return false;
    }
    if (filterSprintScope !== "all") {
      const sid = i.sprint_id;
      const hasSprint = sid != null && sid !== "";
      const st = String(i.sprint_status || "").toLowerCase();
      if (filterSprintScope === "none") {
        if (hasSprint) return false;
      } else if (filterSprintScope === "active") {
        if (!hasSprint || st !== "active") return false;
      } else if (filterSprintScope === "inactive") {
        if (!hasSprint || st === "active") return false;
      }
    }
    // Narrows within the scope above rather than replacing it; "All" is the
    // default so this is a no-op until someone picks a sprint.
    if (filterSprintId !== "All" && String(i.sprint_id) !== String(filterSprintId)) return false;
    if (showClosedOnly && !isClosed(i.status)) return false;
    if (filterReportedDate !== "all") {
      if (!i.createddate) return false;
      if (!matchesCalendarDateFilter(i.createddate, filterReportedDate, filterReportedFrom, filterReportedTo)) {
        return false;
      }
    }
    if (filterResolutionDate !== RESOLVED_STATE_ALL) {
      if (filterResolutionDate === RESOLVED_STATE_VERIFY_CLOSED) {
        if (i.status !== "Closed") return false;
      } else if (filterResolutionDate === RESOLVED_STATE_VERIFY_REOPENED) {
        if (i.status !== "Reopened") return false;
      } else if (VERIFY_CLOSED_DATE_FILTERS[filterResolutionDate]) {
        if (i.status !== "Closed") return false;
        const closedAt = qaVerifiedClosedAt(i);
        if (!closedAt) return false;
        if (!matchesCalendarDateFilter(
          closedAt,
          VERIFY_CLOSED_DATE_FILTERS[filterResolutionDate],
          filterResolvedFrom,
          filterResolvedTo
        )) {
          return false;
        }
      } else {
        if (!isClosed(i.status) || !i.closeddate) return false;
        if (!matchesCalendarDateFilter(i.closeddate, filterResolutionDate, filterResolvedFrom, filterResolvedTo)) {
          return false;
        }
      }
    }
    if (search) {
      const q = search.toLowerCase();
      return (i.description || "").toLowerCase().includes(q) ||
             String(i.issueid).includes(q) ||
             (i.assignee_name || "").toLowerCase().includes(q) ||
             (i.assignee_email || "").toLowerCase().includes(q) ||
             (i.reporter_name || "").toLowerCase().includes(q) ||
             (i.reporter_email || "").toLowerCase().includes(q) ||
             (i.projectname || "").toLowerCase().includes(q) ||
             resolveTeamDisplayName(i.assigneeteam).toLowerCase().includes(q) ||
             canonicalTeamKey(i.assigneeteam).includes(q.replace(/[_-]/g, ' ')) ||
             (i.sprint_name || "").toLowerCase().includes(q);
    }
    return true;
  });

  const hideClosedInTable =
    filterStatus === "All" && !showClosedOnly && filterResolutionDate === RESOLVED_STATE_ALL && filterReportedDate === "all";

  const filtered = hideClosedInTable
    ? filteredCore.filter(i => !isClosed(i.status))
    : filteredCore;

  const visibleTableIssueIds = useMemo(
    () => filtered.map((i) => i.issueid),
    [filtered],
  );

  useEffect(() => {
    if (!selectedId) {
      setModalNavIds(null);
      return;
    }
    // Snapshot visible table rows at open — prev/next follows the same list the user sees.
    setModalNavIds((prev) => {
      if (Array.isArray(prev) && prev.length > 0) return prev;
      return visibleTableIssueIds;
    });
  }, [selectedId, visibleTableIssueIds]);

  const stats = {
    total:    filteredCore.length,
    open:     filteredCore.filter(i => !isClosed(i.status) && i.status !== "Reopened").length,
    blockers: filteredCore.filter(i => i.priority === "Blocker" && !isClosed(i.status)).length,
    resolved: filteredCore.filter(i => isClosed(i.status)).length,
  };

  const hasFilter = search || filterPriority !== "All" || filterStatus !== "All" || filterProject !== "All" ||
    filterAssignee !== "All" || filterReporter !== "All" || filterTeam !== "All" || filterSprintScope !== "all" || filterSprintId !== "All" ||
    showClosedOnly || filterResolutionDate !== RESOLVED_STATE_ALL || filterReportedDate !== "all";

  const teamIssuesBlurb = ["developer", "tester", "designer", "devops", "analyst", "lead", "member"].includes(currentRole);

  /**
   * Sprints of the selected project, newest first — the options for the
   * specific-sprint filter. Released and Closed sprints are included on
   * purpose: looking back at a shipped release's bugs is exactly what this
   * control is for.
   */
  const sprintFilterOptions = useMemo(() => {
    if (filterProject === "All") return [];
    return (sprints || [])
      .filter((s) => String(s.project_id) === String(filterProject))
      .slice()
      .sort((a, b) => String(b.start_date || "").localeCompare(String(a.start_date || "")));
  }, [sprints, filterProject]);

  const orderedIssueProjects = useMemo(() => {
    if (!projects.length) return projects;
    return [...projects].sort((a, b) => {
      const ta = maxBugActivityMs(a.projectid, issues);
      const tb = maxBugActivityMs(b.projectid, issues);
      if (tb !== ta) return tb - ta;
      return String(a.projectname || "").localeCompare(String(b.projectname || ""));
    });
  }, [projects, issues]);

  const issueProjectCarouselStamp = useMemo(
    () => `${orderedIssueProjects.map((p) => p.projectid).join(",")}|${issues.length}`,
    [orderedIssueProjects, issues.length]
  );
  const issueProjectCarouselNav = useIssuesCarouselScrollNav(issueProjectCarouselStamp);
  const issueProjectCarouselCount = 1 + orderedIssueProjects.length;
  const showIssueProjectCarouselArrows = issueProjectCarouselCount >= ISSUES_CAROUSEL_ARROW_MIN;
  const allProjectsSprintCounts = useMemo(
    () => allProjectsActiveSprintBugCounts(issues, sprints),
    [issues, sprints],
  );

  /**
   * Picking a project jumps straight to what the team is working on now —
   * its Active sprint — instead of every bug it has ever had.
   *
   * Only when that sprint exists. A project between sprints has no active
   * one, and scoping to it would show an empty table that looks like a bug;
   * those fall back to "all" so there is always something to read. The
   * dropdown still shows the resulting scope, so the narrowing is visible
   * and one click away from being undone.
   */
  const selectIssueProject = useCallback(
    (projectId) => {
      setProject(projectId);
      // A sprint id belongs to the project it came from; carrying it across
      // would silently filter the new project down to nothing.
      setFilterSprintId("All");
      if (projectId === "All") {
        setFilterSprintScope("all");
        return;
      }
      const hasActiveSprint = (sprints || []).some(
        (s) => String(s.project_id) === String(projectId) && s.status === "Active",
      );
      setFilterSprintScope(hasActiveSprint ? "active" : "all");
    },
    [sprints],
  );

  // Layout already supplies the page padding and the sunken canvas; adding
  // them again here double-padded the page and forced it past the viewport,
  // which is what stopped the sticky header from ever engaging.
  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 20,
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 800, color: C.ink }}>
            Issues & Bugs
          </h1>
          <p style={{ margin: "3px 0 0", color: C.inkSoft, fontSize: "0.82rem" }}>
            {teamIssuesBlurb
              ? "All bugs on projects you are assigned to (team-wide list). Filter by team, assignee, status, or search by name."
              : "Track bugs · assignments · lifecycle · resolutions"}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={refreshAll} style={{
            background: C.surface, color: C.done, border: `1px solid ${C.done}`, padding: "9px 14px",
            borderRadius: 8, fontWeight: 700, fontSize: "0.84rem", cursor: "pointer",
          }}>
            ↻ Refresh
          </button>
          <button onClick={() => setShowForm(true)} style={{
            background: C.accentInk, color: "white", border: "none", padding: "9px 20px",
            borderRadius: 8, fontWeight: 700, fontSize: "0.88rem", cursor: "pointer",
          }}>
            + New Issue
          </button>
        </div>
      </div>

      {/* ── Project Selector (horizontal carousel, newest bug activity first) ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 700, color: C.ink }}>
            📁 Select Project
          </h3>
          <span style={{ fontSize: "0.72rem", color: C.inkFaint, fontWeight: 500 }}>
            {showIssueProjectCarouselArrows
              ? "Projects with the most recently updated bugs appear first. Use the side arrows or drag the row to scroll."
              : "Projects with the most recently updated bugs appear first."}
          </span>
        </div>

        <div style={{ width: "100%", maxWidth: "100%" }}>
          <div style={issuesPickerCarouselShell}>
            <div
              ref={issueProjectCarouselNav.ref}
              style={{
                ...issuesPickerCarouselScrollTrack,
                paddingLeft: showIssueProjectCarouselArrows ? ISSUES_CAROUSEL_EDGE_W : 0,
                paddingRight: showIssueProjectCarouselArrows ? ISSUES_CAROUSEL_EDGE_W : 0,
              }}
            >
              <div style={issuesPickerCarouselInner}>
                {/* All Projects Card */}
                <div
                  onClick={() => selectIssueProject("All")}
                  style={{
                    flex: "0 0 auto",
                    minWidth: 180,
                    padding: "14px 18px",
                    background: filterProject === "All" ? C.accentInk : "#fff",
                    color: filterProject === "All" ? "#fff" : C.ink,
                    border: filterProject === "All" ? `2px solid ${C.accentInk}` : `2px solid ${C.line}`,
                    borderRadius: 12,
                    cursor: "pointer",
                    transition: "all 0.2s",
                    boxShadow: filterProject === "All" ? "0 4px 12px rgba(18, 114, 179, 0.3)" : "0 1px 3px rgba(0,0,0,0.05)",
                  }}
                  onMouseEnter={(e) => {
                    if (filterProject !== "All") {
                      e.currentTarget.style.borderColor = C.accent;
                      e.currentTarget.style.boxShadow = "0 2px 8px rgba(44, 143, 199, 0.2)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (filterProject !== "All") {
                      e.currentTarget.style.borderColor = C.line;
                      e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
                    }
                  }}
                >
                  {/* Name, then the counts on one line. Two rows total, so a
                      long project row stays short and scannable. */}
                  <div style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: 6 }}>
                    🌐 All Projects
                  </div>
                  <div style={{ fontSize: "0.78rem", fontWeight: 700, opacity: 0.85 }}>
                    {allProjectsSprintCounts.open} open · {allProjectsSprintCounts.fixed} fixed
                  </div>
                </div>

                {orderedIssueProjects.map((project) => {
                  const isActive = String(filterProject) === String(project.projectid);
                  const { activeSprint, open, fixed } = activeSprintBugCounts(
                    project.projectid,
                    issues,
                    sprints,
                  );

                  return (
                    <div
                      key={project.projectid}
                      onClick={() => selectIssueProject(project.projectid)}
                      style={{
                        flex: "0 0 auto",
                        minWidth: 200,
                        padding: "14px 18px",
                        background: isActive ? C.accentInk : "#fff",
                        color: isActive ? "#fff" : C.ink,
                        border: isActive ? `2px solid ${C.accentInk}` : `2px solid ${C.line}`,
                        borderRadius: 12,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        boxShadow: isActive ? "0 4px 12px rgba(18, 114, 179, 0.3)" : "0 1px 3px rgba(0,0,0,0.05)",
                        position: "relative",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.borderColor = C.accent;
                          e.currentTarget.style.boxShadow = "0 2px 8px rgba(44, 143, 199, 0.2)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.borderColor = C.line;
                          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.05)";
                        }
                      }}
                    >
                      {/* Name, then the counts on one line — two rows, so the
                          whole carousel stays a compact strip. */}
                      <div
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          marginBottom: 6,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        <span>📦</span>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {project.projectname}
                        </span>
                      </div>
                      <div style={{ fontSize: "0.78rem", fontWeight: 700, opacity: 0.85 }}>
                        {open} open · {fixed} fixed
                      </div>
                      {activeSprint ? (
                        <div
                          style={{
                            fontSize: "0.65rem",
                            opacity: 0.9,
                            marginTop: 6,
                            padding: "2px 6px",
                            background: isActive ? "rgba(255,255,255,0.2)" : C.accentSoft,
                            borderRadius: 4,
                            display: "inline-block",
                            maxWidth: "100%",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          🏃 {activeSprint.sprint_name}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
            {showIssueProjectCarouselArrows && (
              <>
                <button
                  type="button"
                  aria-label="Scroll projects left"
                  disabled={!issueProjectCarouselNav.edge.canLeft}
                  onClick={() => issueProjectCarouselNav.scrollStep(-1)}
                  style={{
                    ...issuesCarouselEdgeButtonBase,
                    left: 0,
                    borderTopLeftRadius: 12,
                    borderBottomLeftRadius: 12,
                    opacity: issueProjectCarouselNav.edge.canLeft ? 1 : 0.42,
                    cursor: issueProjectCarouselNav.edge.canLeft ? "pointer" : "not-allowed",
                    pointerEvents: issueProjectCarouselNav.edge.canLeft ? "auto" : "none",
                  }}
                >
                  ‹
                </button>
                <button
                  type="button"
                  aria-label="Scroll projects right"
                  disabled={!issueProjectCarouselNav.edge.canRight}
                  onClick={() => issueProjectCarouselNav.scrollStep(1)}
                  style={{
                    ...issuesCarouselEdgeButtonBase,
                    right: 0,
                    borderTopRightRadius: 12,
                    borderBottomRightRadius: 12,
                    opacity: issueProjectCarouselNav.edge.canRight ? 1 : 0.42,
                    cursor: issueProjectCarouselNav.edge.canRight ? "pointer" : "not-allowed",
                    pointerEvents: issueProjectCarouselNav.edge.canRight ? "auto" : "none",
                  }}
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Time-Based Stats for Standup */}
      {timeStats && (
        <div style={{ marginBottom: 14 }}>
          <h3 style={{ fontSize: "0.85rem", fontWeight: 700, color: C.ink, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}>
            📊 Bug Tracking
          </h3>
          <p style={{ fontSize: "0.72rem", color: C.inkSoft, margin: "0 0 8px" }}>
            Reported = bugs filed · Resolved = dev closed · Closed = QA verified ({APP_TIMEZONE.replace("_", " ")})
          </p>
          {/* Three cards side by side — period as the heading, its four
              figures beneath. Keeps the numbers next to their own label
              instead of stranding them at the far right of a wide row. */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(230px, 100%), 1fr))", gap: 10 }}>
            {['yesterday', 'today', 'week'].map(period => {
              const data = timeStats[period];
              if (!data) return null;
              const label = period === 'yesterday' ? '📅 Yesterday' : period === 'today' ? '📆 Today' : '📈 This Week';
              return (
                <div key={period} style={{
                  background: C.surface,
                  padding: "11px 13px",
                  borderRadius: RADIUS.card,
                  border: `1px solid ${C.line}`,
                }}>
                  <div style={{ fontSize: "0.75rem", fontWeight: 700, color: C.ink, marginBottom: 9 }}>{label}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    {[
                      { n: data.open, label: "Reported", color: C.warn },
                      { n: data.closed, label: "Resolved", color: C.done },
                      { n: data.verifiedClosed ?? 0, label: "Closed", color: C.accent },
                      { n: data.blockers || 0, label: "Blockers", color: C.danger },
                    ].map((m) => (
                      <div key={m.label} style={{ textAlign: "center", flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: MONO, fontSize: "1.15rem", fontWeight: 800, color: m.color, lineHeight: 1.1 }}>
                          {m.n}
                        </div>
                        <div style={{ fontSize: "0.61rem", color: C.inkFaint, marginTop: 2 }}>{m.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Stats ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(150px, 100%), 1fr))", gap: 8, marginBottom: 12 }}>
        {[
          { label: "Total Issues", value: stats.total,    bg: C.accentSoft, color: C.accentInk, onClick: () => { setStatus("All"); setShowClosedOnly(false); } },
          { label: "Open",         value: stats.open,     bg: C.warnSoft, color: C.warn, onClick: () => { setStatus("Open"); setShowClosedOnly(false); } },
          { label: "Blockers",     value: stats.blockers, bg: C.dangerSoft, color: C.danger, onClick: () => { setPriority("Blocker"); setStatus("All"); setShowClosedOnly(false); } },
          { label: "Resolved",     value: stats.resolved, bg: C.doneSoft, color: C.done, onClick: () => { setShowClosedOnly(true); setStatus("All"); } },
        ].map(s => (
          <div key={s.label} onClick={s.onClick}
            style={{ background: s.bg, borderRadius: 8, padding: "8px 10px", textAlign: "center",
              cursor: "pointer", border: "1px solid transparent", transition: "border 0.15s" }}
            onMouseEnter={e => e.currentTarget.style.border = `1px solid ${s.color}60`}
            onMouseLeave={e => e.currentTarget.style.border = "1px solid transparent"}>
            <div style={{ fontSize: "1.3rem", fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: "0.68rem", color: s.color, fontWeight: 600, marginTop: 1 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={{ background: "white", borderRadius: 10, padding: "12px 14px", marginBottom: 14,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)", display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        {issues.length > 0 && teamOptions.length > 0 && (
          <select
            value={filterTeam}
            onChange={(e) => {
              setFilterTeam(e.target.value);
              setFilterAssignee("All");
            }}
            style={{ ...filterControlStyle, minWidth: 150 }}
          >
            <option value="All">All teams</option>
            {teamOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}

        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search ID, description, team, assignee / reporter name, email, project…"
          style={{ flex: "1 1 200px", padding: "7px 12px", border: `1px solid ${C.line}`,
            borderRadius: 8, fontSize: "0.83rem", background: C.sunken }} />

        {assigneeOptions.length > 0 && (
          <select
            value={filterAssignee}
            onChange={e => setFilterAssignee(e.target.value)}
            style={{ ...filterControlStyle, minWidth: 140 }}
          >
            <option value="All">All assignees</option>
            {assigneeOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}

        {reporterOptions.length > 0 && (
          <select
            value={filterReporter}
            onChange={e => setFilterReporter(e.target.value)}
            style={{ ...filterControlStyle, minWidth: 140 }}
          >
            <option value="All">All reporters</option>
            {reporterOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}

        <select
          value={filterSprintScope}
          onChange={(e) => setFilterSprintScope(e.target.value)}
          style={{ ...filterControlStyle, minWidth: 200 }}
          title="Filter by sprint lifecycle (Jira-style: completed sprint bugs stay on the sprint until moved)"
        >
          <option value="all">All sprint scopes</option>
          <option value="active">Active sprint only</option>
          <option value="inactive">Inactive / completed sprint</option>
          <option value="none">Backlog (no sprint)</option>
        </select>

        {/*
          Pick one named sprint, for looking at a specific release's bugs
          without reasoning about lifecycle. Only offered once a project is
          chosen: across all projects the sprint names are not unique enough to
          mean anything, and the list would be enormous.

          It narrows whatever the scope dropdown already selected rather than
          replacing it, so the default view — the active sprint — is unchanged
          until someone picks a sprint here.
        */}
        {filterProject !== "All" && sprintFilterOptions.length > 0 && (
          <select
            value={filterSprintId}
            onChange={(e) => setFilterSprintId(e.target.value)}
            style={{ ...filterControlStyle, minWidth: 170 }}
            title="Show bugs from one specific sprint"
          >
            <option value="All">All sprints</option>
            {sprintFilterOptions.map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.sprint_name} ({s.status})
              </option>
            ))}
          </select>
        )}

        {/* Priority chips */}
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
          {PRIORITY_FILTERS.map(p => {
            const meta = PRIORITY_META[p];
            const active = filterPriority === p;
            return (
              <button key={p} onClick={() => setPriority(p)} style={{
                padding: "4px 11px", borderRadius: 20, border: "2px solid",
                borderColor: active ? (meta?.dot || C.ink) : C.line,
                background: active ? (meta?.bg || C.sunken) : "white",
                color: active ? (meta?.color || C.ink) : C.inkSoft,
                fontWeight: active ? 700 : 500, fontSize: "0.76rem", cursor: "pointer",
              }}>
                {p === "All" ? "All Priority" : p}
              </button>
            );
          })}
        </div>

        {/* Status dropdown */}
        <select value={filterStatus} onChange={e => setStatus(e.target.value)}
          style={{ ...filterControlStyle }}>
          <option value="All">All except resolved</option>
          <option value={STATUS_ALL_INCLUDING_CLOSED}>All including resolved</option>
          {Object.entries(STATUS_FILTER_GROUPS).map(([group, statuses]) => (
            <optgroup key={group} label={group}>
              {statuses.map(s => (
                <option key={s} value={s}>{s === "Reopened" ? "Reopen" : s}</option>
              ))}
            </optgroup>
          ))}
        </select>

        <select
          value={filterReportedDate}
          onChange={(e) => {
            const next = e.target.value;
            setFilterReportedDate(next);
            if (next !== "custom") {
              setFilterReportedFrom("");
              setFilterReportedTo("");
            }
          }}
          style={{ ...filterControlStyle, minWidth: 150 }}
          title="Filter by bug filed date (Raised column)"
        >
          <option value="all">Any filed date</option>
          <option value="today">Filed today</option>
          <option value="yesterday">Filed yesterday</option>
          <option value="week">Filed this week</option>
          <option value="custom">Custom filed range</option>
        </select>

        {filterReportedDate === "custom" && (
          <>
            <input
              type="date"
              value={filterReportedFrom}
              onChange={(e) => setFilterReportedFrom(e.target.value)}
              style={{ ...filterControlStyle }}
              title="Filed from date"
            />
            <span style={{ color: C.inkFaint, fontSize: "0.78rem" }}>to</span>
            <input
              type="date"
              value={filterReportedTo}
              onChange={(e) => setFilterReportedTo(e.target.value)}
              style={{ ...filterControlStyle }}
              title="Filed to date"
            />
          </>
        )}

        <select
          value={filterResolutionDate}
          onChange={(e) => {
            const next = e.target.value;
            setFilterResolutionDate(next);
            if (next !== "custom") {
              setFilterResolvedFrom("");
              setFilterResolvedTo("");
            }
          }}
          style={{ ...filterControlStyle, minWidth: 150 }}
          title="Filter by resolved date or QA verified state"
        >
          <option value={RESOLVED_STATE_ALL}>Any resolved state</option>
          <optgroup label="Resolved date">
            <option value="today">Resolved today</option>
            <option value="yesterday">Resolved yesterday</option>
            <option value="week">Resolved this week</option>
            <option value="custom">Custom resolved range</option>
          </optgroup>
          <optgroup label="Verified">
            <option value={RESOLVED_STATE_VERIFY_CLOSED}>Closed</option>
            <option value={RESOLVED_STATE_VERIFY_CLOSED_TODAY}>Closed today</option>
            <option value={RESOLVED_STATE_VERIFY_CLOSED_YESTERDAY}>Closed yesterday</option>
            <option value={RESOLVED_STATE_VERIFY_CLOSED_WEEK}>Closed this week</option>
            <option value={RESOLVED_STATE_VERIFY_REOPENED}>Reopen</option>
          </optgroup>
        </select>

        {filterResolutionDate === "custom" && (
          <>
            <input
              type="date"
              value={filterResolvedFrom}
              onChange={(e) => setFilterResolvedFrom(e.target.value)}
              style={{ ...filterControlStyle }}
              title="Resolved from date"
            />
            <span style={{ color: C.inkFaint, fontSize: "0.78rem" }}>to</span>
            <input
              type="date"
              value={filterResolvedTo}
              onChange={(e) => setFilterResolvedTo(e.target.value)}
              style={{ ...filterControlStyle }}
              title="Resolved to date"
            />
          </>
        )}

        {hasFilter && (
          <button onClick={() => {
            setSearch("");
            setPriority("All");
            setStatus("All");
            setProject("All");
            setFilterAssignee("All");
            setFilterReporter("All");
            setFilterTeam("All");
            setFilterSprintScope("all");
            setFilterSprintId("All");
            setShowClosedOnly(false);
            setFilterReportedDate("all");
            setFilterReportedFrom("");
            setFilterReportedTo("");
            setFilterResolutionDate(RESOLVED_STATE_ALL);
            setFilterResolvedFrom("");
            setFilterResolvedTo("");
          }}
            style={{ padding: "5px 10px", borderRadius: 7, border: `1px solid ${C.line}`,
              background: C.sunken, color: C.inkSoft, fontSize: "0.78rem", cursor: "pointer" }}>
            Clear
          </button>
        )}
      </div>

      {/* ── Table ── */}
      {loading ? (
        // Table-shaped: a header bar then rows, so the page doesn't collapse
        // to a single line of text and then snap back to full height.
        <div
          role="status"
          aria-busy="true"
          aria-label="Loading issues"
          style={{ background: "white", borderRadius: 12, padding: 12, display: "flex", flexDirection: "column", gap: 8 }}
        >
          <Skeleton height={34} radius={8} />
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} height={40} radius={6} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "3rem", background: "white", borderRadius: 12, color: C.inkSoft }}>
          {issues.length === 0
            ? (["admin", "superadmin", "manager", "product", "product_manager", "lead"].includes(currentRole)
              ? "No bugs loaded yet. Use + New Issue (type Bug) to create one."
              : "No bugs loaded for your projects. Confirm you are assigned to the project and that items are type Bug (stories/tasks live on Sprint Board).")
            : "No issues match your filters."}
        </div>
      ) : (
        // Its own scroll box rather than pushing the filters off the top.
        // Height is capped against the viewport so the column headers —
        // sticky inside — stay put however far you scroll.
        <div
          style={{
            background: C.surface,
            borderRadius: RADIUS.card,
            border: `1px solid ${C.line}`,
            overflow: "auto",
            maxHeight: "calc(100vh - 260px)",
            minHeight: 240,
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1120 }}>
            <thead>
              <tr style={{ color: "white" }}>
                {["ID", "Priority", "Summary", "Reporter", "Assignee", "Team", "Project", "Sprint", "Raised", "Closed", "Status", "Resolution", "Actions"].map((h, hi) => (
                  // Sticky lives on the cells, not the row — a sticky <tr>
                  // leaves the background behind and rows show through it.
                  <th key={`${h}-${hi}`} style={{ padding: "10px 13px", textAlign: "left", fontSize: "0.74rem",
                    fontWeight: 700, whiteSpace: "nowrap", letterSpacing: "0.03em",
                    position: "sticky", top: 0, zIndex: 2, background: C.accentInk }}>{h === "Actions" ? "" : h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((issue, idx) => {
                const qaVerifiedClosed = isBugQaVerifiedClosed(issue.status);
                const summary = parseIssueDescription(issue.description).title || '(No description)';
                const isBlocker = issue.priority === "Blocker";
                // One variable, read both as the resting style and as the
                // hover restore below — keep it that way or hovered rows keep
                // whichever colour they were last given.
                const rowBg = isBlocker && !qaVerifiedClosed ? C.dangerSoft : idx % 2 === 0 ? C.surface : C.sunken;
                return (
                  <tr key={issue.issueid}
                    onClick={() => setSelectedId(issue.issueid)}
                    style={{ borderBottom: `1px solid ${C.sunken}`, background: rowBg, cursor: "pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background = C.accentSoft}
                    onMouseLeave={e => e.currentTarget.style.background = rowBg}
                  >
                    <td style={{ padding: "9px 13px", fontSize: "0.76rem", color: C.inkSoft, fontWeight: 700 }}>
                      #{issue.issueid}
                    </td>
                    <td style={{ padding: "9px 13px" }}><PriorityBadge priority={issue.priority} /></td>
                    {/* The summary is the one column people actually read, so
                        it gets two lines and the width the other columns do
                        not need. -webkit-line-clamp still ellipsises, just at
                        the end of line two instead of line one. */}
                    <td style={{ padding: "9px 13px", width: 340, minWidth: 260, maxWidth: 340 }}>
                      <span
                        title={summary}
                        style={{ fontSize: "0.83rem", color: qaVerifiedClosed ? C.inkFaint : C.ink,
                          textDecoration: qaVerifiedClosed ? "line-through" : "none",
                          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                          overflow: "hidden", lineHeight: 1.35, overflowWrap: "anywhere" }}>
                        {summary}
                      </span>
                    </td>
                    <td style={{ padding: "9px 13px", fontSize: "0.78rem", color: C.inkSoft, whiteSpace: "nowrap" }}>
                      {issue.reporter_name || issue.reporter_email || <span style={{ color: C.upcoming }}>—</span>}
                    </td>
                    <td style={{ padding: "9px 13px", fontSize: "0.78rem", color: C.ink, whiteSpace: "nowrap" }}>
                      {issue.assignee_name || issue.assignee_email || <span style={{ color: C.upcoming }}>Unassigned</span>}
                    </td>
                    <td style={{ padding: "9px 13px", fontSize: "0.78rem", color: C.inkSoft, whiteSpace: "nowrap", maxWidth: 120 }}>
                      {issue.assigneeteam ? (
                        <span style={{ fontWeight: 600, color: C.ink }}>{resolveTeamDisplayName(issue.assigneeteam)}</span>
                      ) : (
                        <span style={{ color: C.upcoming }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "9px 13px", fontSize: "0.78rem", color: C.ink, whiteSpace: "nowrap" }}>
                      {issue.projectname || issue.projectid}
                    </td>
                    <td style={{ padding: "9px 13px", fontSize: "0.74rem", color: C.inkSoft, maxWidth: 160 }}>
                      {issue.sprint_name ? (
                        <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>
                          {issue.sprint_name}
                          {issue.sprint_status && String(issue.sprint_status).toLowerCase() !== "active" && (
                            <span style={{ fontWeight: 700, color: C.inkFaint, marginLeft: 4, fontSize: "0.66rem" }}>
                              ({issue.sprint_status})
                            </span>
                          )}
                        </span>
                      ) : (
                        <span style={{ color: C.upcoming }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "9px 13px", fontSize: "0.74rem", color: C.inkSoft, whiteSpace: "nowrap" }}>
                      <div>{fmtDate(issue.createddate)}</div>
                      <div style={{ color: C.inkFaint, fontSize: "0.68rem" }}>{daysAgo(issue.createddate)}</div>
                    </td>
                    <td style={{ padding: "9px 13px", fontSize: "0.74rem", whiteSpace: "nowrap" }}>
                      {issue.closeddate
                        ? <span style={{ color: C.done }}>{fmtDate(issue.closeddate)}</span>
                        : <span style={{ color: C.upcoming }}>—</span>}
                    </td>
                    <td style={{ padding: "9px 13px" }}><StatusBadge status={issue.status} /></td>
                    <td style={{ padding: "9px 13px", fontSize: "0.74rem", color: C.inkSoft, whiteSpace: "nowrap" }}>
                      {issue.resolution
                        ? <span style={{ color: RESOLUTION_META[issue.resolution]?.color || C.ink, fontWeight: 600 }}>
                            {RESOLUTION_META[issue.resolution]?.icon} {issue.resolution}
                          </span>
                        : <span style={{ color: C.upcoming }}>—</span>}
                    </td>
                    <td style={{ padding: "9px 13px" }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: "flex", gap: 5 }}>
                        <button onClick={e => { e.stopPropagation(); setSelectedId(issue.issueid); }}
                          style={{ padding: "3px 9px", borderRadius: 5, border: `1px solid ${C.line}`,
                            background: "white", fontSize: "0.72rem", cursor: "pointer", color: C.ink }}>
                          Open
                        </button>
                        {isAdmin && (
                          <button onClick={e => handleDelete(issue.issueid, e)}
                            style={{ padding: "3px 8px", borderRadius: 5, border: `1px solid ${C.dangerSoft}`,
                              background: C.dangerSoft, fontSize: "0.72rem", cursor: "pointer", color: C.danger }}>
                            Del
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ padding: "9px 14px", background: C.sunken, borderTop: `1px solid ${C.sunken}`,
            fontSize: "0.76rem", color: C.inkSoft }}>
            Showing {filtered.length} of {issues.length} issues
          </div>
        </div>
      )}

      {/* ── Issue Detail Modal ── */}
      {selectedId && (
        <IssueDetailModal
          issueId={selectedId}
          users={users}
          navigationIds={modalNavIds ?? visibleTableIssueIds}
          onNavigate={(id) => setSelectedId(String(id))}
          onClose={() => setSelectedId(null)}
          onStatusChanged={handleStatusChanged}
          onIssueUpdated={load}
        />
      )}

      {/* ── Create Issue Modal ── */}
      {showForm && (
        <div style={modalBackdropStyle}>
          <div style={issueFormModalPanelStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800 }}>New Issue</h2>
              <button onClick={() => setShowForm(false)}
                style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.inkSoft }}>✕</button>
            </div>
            <IssueForm
              fixedType="Bug"
              autoGrowDescription
              onSuccess={() => { setShowForm(false); load(); toast.success("Issue created!"); }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Issues;
